import { supabase } from '../lib/supabase';

export const newsletterService = {
  /**
   * Subscribe an email to the newsletter
   */
  async subscribe(email) {
    try {
      const { data, error } = await supabase
        ?.from('newsletter_subscriptions')
        ?.insert({
          email: email?.trim()?.toLowerCase(),
          source: 'footer_signup',
          status: 'active'
        })
        ?.select()
        ?.single();

      if (error) {
        // Handle duplicate email error gracefully
        if (error?.code === '23505') {
          throw new Error('This email is already subscribed to our newsletter');
        }
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      throw new Error(err?.message || 'Failed to subscribe to newsletter');
    }
  },

  /**
   * Subscribe a logged-in user to newsletter (called from profile settings)
   */
  async subscribeUser(userEmail) {
    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        ?.from('newsletter_subscriptions')
        ?.select('id, status')
        ?.eq('email', userEmail?.trim()?.toLowerCase())
        ?.single();

      if (existing) {
        // If exists but unsubscribed, reactivate
        if (existing?.status !== 'active') {
          const { data, error } = await supabase
            ?.from('newsletter_subscriptions')
            ?.update({ status: 'active', updated_at: new Date()?.toISOString() })
            ?.eq('email', userEmail?.trim()?.toLowerCase())
            ?.select()
            ?.single();

          if (error) throw error;
          return data;
        }
        return existing;
      }

      // Create new subscription
      const { data, error } = await supabase
        ?.from('newsletter_subscriptions')
        ?.insert({
          email: userEmail?.trim()?.toLowerCase(),
          source: 'profile_settings',
          status: 'active'
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      throw new Error(err?.message || 'Failed to subscribe to newsletter');
    }
  },

  /**
   * Unsubscribe a logged-in user from newsletter
   */
  async unsubscribeUser(userEmail) {
    try {
      const { data, error } = await supabase
        ?.from('newsletter_subscriptions')
        ?.update({ status: 'inactive', updated_at: new Date()?.toISOString() })
        ?.eq('email', userEmail?.trim()?.toLowerCase())
        ?.select()
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Newsletter unsubscribe error:', err);
      throw new Error(err?.message || 'Failed to unsubscribe from newsletter');
    }
  },

  /**
   * Get member statistics (users + newsletter subscribers)
   */
  async getMemberStatistics() {
    try {
      const { data, error } = await supabase?.rpc('get_member_statistics');

      if (error) throw error;

      if (!data || data?.length === 0) {
        return {
          totalUsers: 0,
          newsletterSubscribers: 0,
          totalMembers: 0
        };
      }

      const stats = data?.[0];
      return {
        totalUsers: stats?.total_users || 0,
        newsletterSubscribers: stats?.newsletter_subscribers || 0,
        totalMembers: stats?.total_members || 0
      };
    } catch (err) {
      console.error('Error fetching member statistics:', err);
      return {
        totalUsers: 0,
        newsletterSubscribers: 0,
        totalMembers: 0
      };
    }
  },

  /**
   * Check if email is already subscribed
   */
  async checkSubscription(email) {
    try {
      const { data, error } = await supabase?.from('newsletter_subscriptions')?.select('status')?.eq('email', email?.trim()?.toLowerCase())?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return data ? data?.status === 'active' : false;
    } catch (err) {
      console.error('Error checking subscription:', err);
      return false;
    }
  },

  /**
   * Unsubscribe from newsletter
   */
  async unsubscribe(email) {
    try {
      const { data, error } = await supabase?.from('newsletter_subscriptions')?.update({ status: 'unsubscribed' })?.eq('email', email?.trim()?.toLowerCase())?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Newsletter unsubscribe error:', err);
      throw new Error('Failed to unsubscribe from newsletter');
    }
  }
};